/**
 * A transcript, read as a conversation.
 *
 * The words are the same words whether a transcription lane produced them from a recording or a
 * person uploaded a subtitle or Word file already holding them: the row's `contentShape` is what
 * decides that this dialog is what opens, and everything below reads the transcript query rather
 * than the file behind it.
 *
 * ── Why it fills the screen ──────────────────────────────────────────────────
 * A meeting is thousands of turns read top to bottom in one sitting, so the dialog takes the
 * sizing the conflicted-changes review uses (`features/project/components/entity-override-dialog.tsx`):
 * a full-screen sheet below 840px, a wide centred panel above it, header and footer pinned and
 * the reading column scrolling between them. Anything smaller would be a peephole onto an hour of
 * speech.
 *
 * ── Two presentations, one rule ──────────────────────────────────────────────
 * The whole layout forks on whether the vendor told the speakers apart, because chrome that says
 * nothing is worse than no chrome:
 *
 * - **Diarized** — every turn is a labelled block with a per-speaker accent, so a glance down the
 *   column shows the turn-taking without reading a word. The accent is deterministic in the
 *   speaker's own number and comes from the speaker palette (`lib/speaker-palette.ts`), ten
 *   identities the token file declares.
 * - **Undiarized** — the same reading column with no label and no accent at all, because
 *   "Speaker ?" over every paragraph is a column of noise. A timecode, where the turns carry one,
 *   still rides subtly beside the words: it is the only thing left that says where in the
 *   recording a passage is.
 *
 * ── What the transcript is, stated once ─────────────────────────────────────
 * How long it runs, how many voices were told apart and which languages were identified are facts
 * *about* the document rather than words of it, so the header states them as labelled pairs and
 * the reading column opens on the first turn. Each pair is omitted where the fact is not known,
 * because a label over nothing is a question the header cannot answer. The transcript's own
 * opening note says the same things as a sentence and leads the downloaded file, where there is
 * no header to carry them.
 *
 * ── Names, and what they are ─────────────────────────────────────────────────
 * A speaker can be given a name from the legend strip. That name is a **label the server keeps
 * beside the words**, on the transcription these turns belong to: nothing in the stored transcript
 * changes, so every label on screen — the legend chip, the turn header, the timeline row — reads it
 * through one helper (`transcriptSpeakerDisplayLabel`), and a rename lands on all three in the same
 * paint. A transcription the recording no longer reads as cannot be renamed at all: its numbers
 * describe nobody on screen, and the refusal says to reopen the transcript.
 *
 * ── Loading ──────────────────────────────────────────────────────────────────
 * Pages append as they land. The first screenful is the point of opening the dialog, so it renders
 * the moment it arrives with a quiet line saying more is coming, instead of holding a blank panel
 * until the last page of a five-hour recording has been read.
 *
 * ── Listening, where there is something to listen to ─────────────────────────
 * **`playableMedia` is the one switch.** It comes off the transcript query — a succeeded
 * transcription that kept the audio it derived — and never off a file extension or a document
 * kind, so the player is offered exactly where an object exists to sign. Where it is false this
 * dialog is precisely what it was: plain timestamps, a footer of two buttons, no playhead.
 *
 * Where it is true, the transcript, the activity timeline and the player bar are **three views of
 * one clock**. `player.currentTime` decides which turn is highlighted, where the playhead sits and
 * what the elapsed readout says, and each of the three can write to it — a press on a turn, a drag
 * of the scrubber, a click on a timeline track. Nothing opens a second player and nothing
 * navigates away.
 *
 * ── Following yields to the reader ──────────────────────────────────────────
 * The column scrolls itself to the turn being spoken, and stops doing so the instant somebody
 * scrolls it themselves — a surface that scrolls back is a surface fighting the person using it.
 * A pill then offers to take them back, and the next explicit seek re-engages following on its
 * own, because asking to be somewhere is asking to be taken there. A citation opening the dialog
 * is that same intent stated up front: for a timed turn it is a seek, so the clock stands at the
 * cited turn and a press plays the words being cited, with the column landed where following
 * keeps the spoken turn; a turn with no time is landed on with following suspended, and the clock
 * — at the start, or wherever the recording was last left — does not carry the column off it.
 *
 * Every one of those movements **travels**, and it travels under this dialog's own animation
 * (`lib/animated-scroll.ts`) rather than the browser's `behavior: "smooth"`, which is abandoned by
 * anything that writes the scroll offset or reshapes the column while it runs — a page landing, a
 * long turn being scrolled through, the clock repainting the active turn. Abandoned, it reads as
 * the column jumping, and a reader who jumps loses their place. The one deliberate exception is a
 * citation opening the dialog: there is nothing on screen to travel from, so it lands.
 */

import type { Id } from "@repo/convex/dataModel";
import {
  formatTranscriptTurnTimecode,
  type TranscriptSpeakerNames,
  transcriptSpeakerDisplayLabel,
} from "@repo/shared";
import { DownloadIcon, XIcon } from "lucide-react";
import {
  Fragment,
  memo,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LoadingRow } from "@/components/ui/loading-state";
import { formatDuration, formatLanguageNames } from "@/lib/formatters";
import { cn } from "@/lib/utils";
import { useAnimatedScroll } from "../hooks/use-animated-scroll";
import { useRememberedTimelineExpansion } from "../hooks/use-remembered-timeline-expansion";
import { useTranscriptDocument } from "../hooks/use-transcript-document";
import { useTranscriptPlayer } from "../hooks/use-transcript-player";
import { useTranscriptSpeakerNames } from "../hooks/use-transcript-speaker-names";
import { recordingClass } from "../lib/document-processing-view";
import { buildSpeakerActivity } from "../lib/speaker-activity";
import { speakerPaletteStyle } from "../lib/speaker-palette";
import {
  playableTranscriptTurns,
  resolveActiveTurn,
  resolveFollowScrollTop,
  resolveTurnProgress,
} from "../lib/transcript-playback";
import type { TranscriptTurn } from "../lib/transcript-turns-view";
import { TranscriptActivityPanel } from "./transcript-activity-panel";
import { TranscriptPlayerBar } from "./transcript-player-bar";
import { TranscriptSpeakerLegend } from "./transcript-speaker-legend";
import { TranscriptTurnPlayButton } from "./transcript-turn-play-button";

export interface TranscriptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: Id<"projects">;
  /** The document whose words are being read. `null` closes the dialog and stops the paging. */
  artifactId: string | null;
  /**
   * The transcription to open. An evidence citation names the one it was made against, so the
   * turn it quotes is the turn on screen; the Documents row names none and reads the transcript
   * the document reads as now. A named transcription that is gone reloads visibly onto the
   * current one.
   */
  processingRevisionId?: string | null;
  /** The row's own name, so the header is filled before the first page lands. */
  name: string;
  /**
   * The length the row already knows — a recording's, probed long before a word was transcribed.
   * A transcript file's row knows none, and the length comes back with the blocks instead.
   */
  durationSeconds?: number | null;
  /**
   * The block a citation points at, within the transcription it names. The dialog opens on the
   * turn that block belongs to and marks it briefly; `null` opens at the top, which is what a
   * citation of a revision the document no longer reads as says.
   */
  focusBlockIndex?: number | null;
  /** Save the transcript as a text file — the same implementation the ⋯ menu's item calls. */
  onDownload: () => void;
  /** The file is being assembled right now. */
  isDownloading: boolean;
}

/** How long a jumped-to turn stays marked, so the eye can find where it landed. */
const JUMP_HIGHLIGHT_MS = 1_600;

/** How far a bare arrow key moves the recording, and how far a shifted one does. */
const SEEK_STEP_SECONDS = 5;
const SEEK_STEP_LARGE_SECONDS = 30;

/**
 * Whether the reader has asked for less motion.
 *
 * Read imperatively rather than through a media-query hook, because it is not a layout decision:
 * nothing about the dialog's shape depends on it. What depends on it is whether the column
 * travels to the next turn or arrives there, and whether the rail grows or steps.
 */
function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return reduced;
}

/** A key press the dialog must leave alone, because it is somebody typing. */
function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/**
 * Which turn holds a cited block, over the turns loaded so far.
 *
 * A turn's key is the index of the block it *started* at, and the store splits a long turn into
 * continuation blocks that carry no header of their own — so a citation of a continuation block
 * belongs to the last turn that started at or before it, never to the next one down the page.
 *
 * `reached` says the loaded turns already run past the block, which is what lets the caller tell
 * "the turn is not here yet" from "there is no such turn". The turns are in block order, so the
 * walk stops at the first one that reaches the index.
 */
export function locateFocusTurn(
  turns: readonly TranscriptTurn[],
  blockIndex: number
): { turnKey: number | null; reached: boolean } {
  let turnKey: number | null = null;
  for (const turn of turns) {
    if (turn.key <= blockIndex) turnKey = turn.key;
    if (turn.key >= blockIndex) return { turnKey, reached: true };
  }
  return { turnKey, reached: false };
}

export function TranscriptDialog({
  open,
  onOpenChange,
  projectId,
  artifactId,
  processingRevisionId,
  name,
  durationSeconds,
  focusBlockIndex,
  onDownload,
  isDownloading,
}: TranscriptDialogProps) {
  const transcript = useTranscriptDocument(
    projectId,
    open ? artifactId : null,
    processingRevisionId
  );
  const { document } = transcript;
  // Everything about these words names the transcription they came from, never the document's
  // newest: the speaker numbers, the names given to them, and the audio behind them are all that
  // run's own.
  const pinnedRevisionId = transcript.processingRevisionId;
  const { speakerNames, renameSpeaker, isRenaming } = useTranscriptSpeakerNames(
    projectId,
    open ? artifactId : null,
    pinnedRevisionId,
    transcript.speakerNames
  );

  const lengthSeconds = transcript.durationSeconds ?? durationSeconds ?? null;

  /** Every speaker in the transcript, in the order they first speak. */
  const speakers = useMemo(() => {
    const seen: number[] = [];
    for (const turn of document.turns) {
      const speaker = turn.header?.speaker;
      if (speaker !== undefined && !seen.includes(speaker)) seen.push(speaker);
    }
    return seen;
  }, [document]);

  const activity = useMemo(
    () => buildSpeakerActivity(document.turns, lengthSeconds),
    [document, lengthSeconds]
  );

  /** The header's labelled facts, in reading order, carrying only the ones this transcript has. */
  const meta = useMemo(() => {
    const pairs: { key: string; label: string; value: string }[] = [];
    if (lengthSeconds !== null && lengthSeconds > 0) {
      pairs.push({
        key: "length",
        label: "Length",
        value: formatDuration(lengthSeconds * 1_000, { style: "media" }),
      });
    }
    if (speakers.length > 0) {
      pairs.push({ key: "speakers", label: "Speakers", value: String(speakers.length) });
    }
    const languages = formatLanguageNames(transcript.languages);
    if (languages !== null) pairs.push({ key: "language", label: "Language", value: languages });
    return pairs;
  }, [lengthSeconds, speakers.length, transcript.languages]);

  const hasTurns = document.turns.length > 0;

  const { isExpanded, setExpanded } = useRememberedTimelineExpansion(open ? artifactId : null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const turnRefs = useRef(new Map<number, HTMLElement>());
  const heldScrollTop = useRef<number | null>(null);
  const [jumpedTurnKey, setJumpedTurnKey] = useState<number | null>(null);

  // The one switch. The query answers it off the run that produced these words, so a recording
  // whose derived audio was never kept offers nothing rather than a control that would refuse.
  const hasPlayableMedia = open && transcript.playableMedia === true;
  const reducedMotion = useReducedMotion();

  const playableTurns = useMemo(() => playableTranscriptTurns(document.turns), [document]);
  const player = useTranscriptPlayer({
    projectId,
    artifactId: open ? artifactId : null,
    processingRevisionId: pinnedRevisionId,
    enabled: hasPlayableMedia,
    durationSeconds: lengthSeconds,
    // What the *upload* is, from the same classifier the ⋯ menu decides its two downloads with.
    // It is what says whether a browser that cannot decode the derived audio has anything to fall
    // back to: an audio upload does, a video upload does not.
    originalMediaClass: recordingClass({ fileName: transcript.fileName ?? undefined }),
    turns: playableTurns,
  });
  const isPlaybackUnsupported = player.unavailableReason === "unsupported_format";

  const activeTurn = hasPlayableMedia ? resolveActiveTurn(playableTurns, player.currentTime) : null;
  const activeTurnKey = activeTurn === null ? null : activeTurn.key;
  // Reduced motion steps the rail per turn instead of growing it: the fill still says which turn
  // is being spoken, without a bar that animates for the length of a monologue.
  const activeProgress =
    activeTurn === null
      ? 0
      : reducedMotion
        ? 1
        : resolveTurnProgress(activeTurn, player.currentTime);
  const isSounding = player.status === "playing" || player.status === "buffering";

  // Opening and closing the panel changes the reading column's height, not its content, so the
  // turn somebody was reading has to stay exactly where it was: the offset is taken before the
  // toggle and put back before the browser paints the new layout.
  const toggleTimeline = useCallback(() => {
    heldScrollTop.current = scrollRef.current?.scrollTop ?? null;
    setExpanded(!isExpanded);
  }, [isExpanded, setExpanded]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the panel's state is the trigger rather than an input — the restore has to run after the layout the toggle produced, and a ref schedules nothing.
  useLayoutEffect(() => {
    if (heldScrollTop.current === null || scrollRef.current === null) return;
    scrollRef.current.scrollTop = heldScrollTop.current;
    heldScrollTop.current = null;
  }, [isExpanded]);

  const scroll = useAnimatedScroll(scrollRef);

  // A jump puts the turn's header line at the top of what is visible — the reading column's own
  // viewport, which is shorter while the activity panel is open, so the alignment is against the
  // column and not the dialog. The column's top padding is kept above the turn, so the header
  // sits where any turn at the top of the column sits rather than flush against the edge.
  //
  // Measured rather than captured: the animation asks again on every frame, and a page landing
  // above the turn while the column travels moves where it has to end up.
  const measureTurnTop = useCallback((turnKey: number): number | null => {
    const scroller = scrollRef.current;
    const target = turnRefs.current.get(turnKey);
    if (!scroller || !target) return null;
    const paddingTop = Number.parseFloat(window.getComputedStyle(scroller).paddingTop) || 0;
    return Math.max(
      0,
      target.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop -
        paddingTop
    );
  }, []);

  // The timeline travels, because the reader is watching the column they are already in; a
  // citation opening the dialog lands instantly, because there is nothing on screen to travel from.
  const jumpToTurn = useCallback(
    (turnKey: number, options?: { instant?: boolean }) => {
      const destination = () => measureTurnTop(turnKey);
      if (options?.instant === true || reducedMotion) scroll.jumpTo(destination);
      else void scroll.animateTo(destination);
      setJumpedTurnKey(turnKey);
    },
    [measureTurnTop, reducedMotion, scroll]
  );

  useEffect(() => {
    if (jumpedTurnKey === null) return;
    const timer = window.setTimeout(() => setJumpedTurnKey(null), JUMP_HIGHLIGHT_MS);
    return () => window.clearTimeout(timer);
  }, [jumpedTurnKey]);

  // ── Following ────────────────────────────────────────────────────────────
  const citedBlockIndex = typeof focusBlockIndex === "number" ? focusBlockIndex : null;
  // Suspended from the first paint when the dialog opened on a citation: the reader came to read
  // that turn, and the clock — at the recording's start, or wherever it was last left — must not
  // carry the column away from it while the pages holding it are still arriving. The landing
  // below decides what re-engages it. The initial value matters, not only the landing's own
  // write: the follow effect is a passive effect, and React runs the passive effects of a commit
  // before it renders the state a layout effect of that same commit set.
  const [followSuspended, setFollowSuspended] = useState(citedBlockIndex !== null);
  /** What the column last followed, so a re-render that changed nothing scrolls nothing. */
  const followed = useRef<{ turnKey: number | null; seekNonce: number }>({
    turnKey: null,
    seekNonce: 0,
  });

  /**
   * Where the column has to be for the turn being spoken to be under the reader's eye.
   *
   * A turn that fits is anchored ~100px below the top of the column and stays there; one taller
   * than the column travels through itself at the rate it is being spoken. Both shapes are one
   * function (`resolveFollowScrollTop`), so a long turn is not a second scrolling rule.
   */
  const measureFollowTop = useCallback((turnKey: number, progress: number): number | null => {
    const scroller = scrollRef.current;
    const target = turnRefs.current.get(turnKey);
    if (!scroller || !target) return null;
    const blockTop =
      target.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop;
    return resolveFollowScrollTop({
      blockTop,
      blockHeight: target.offsetHeight,
      viewportHeight: scroller.clientHeight,
      progress,
    });
  }, []);

  const scrollToFollow = useCallback(
    (turnKey: number, progress: number, instant: boolean) => {
      const destination = () => measureFollowTop(turnKey, progress);
      if (instant) scroll.jumpTo(destination);
      else void scroll.animateTo(destination);
    },
    [measureFollowTop, scroll]
  );

  // Any scroll the *reader* makes suspends following at once, and stops whatever travel is in
  // flight in the same breath: waiting for a tween to finish would drag the column on past the
  // moment somebody took it over. Listening for the gestures rather than for `scroll` is what
  // makes this exact: our own travel fires neither a wheel nor a touch nor a key, so there is no
  // window in which our scroll could be mistaken for theirs and none in which theirs is missed. A
  // pointer pressed on the scroll container itself — not on a turn inside it — is the scrollbar,
  // the one scroll gesture that fires none of the other three.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (scroller === null || !hasPlayableMedia) return;
    const suspend = () => {
      scroll.stop();
      setFollowSuspended(true);
    };
    const suspendOnScrollbar = (event: PointerEvent) => {
      if (event.target === scroller) suspend();
    };
    scroller.addEventListener("wheel", suspend, { passive: true });
    scroller.addEventListener("touchmove", suspend, { passive: true });
    scroller.addEventListener("keydown", suspend);
    scroller.addEventListener("pointerdown", suspendOnScrollbar);
    return () => {
      scroller.removeEventListener("wheel", suspend);
      scroller.removeEventListener("touchmove", suspend);
      scroller.removeEventListener("keydown", suspend);
      scroller.removeEventListener("pointerdown", suspendOnScrollbar);
    };
  }, [hasPlayableMedia, scroll]);

  // ── Landing on a citation ────────────────────────────────────────────────
  // A citation names a block, and the dialog opens on the turn holding it — once per opening,
  // which is what `landedOpening` records. The pages arrive one at a time, so the landing waits
  // until the turns either reach the block or stop coming: jumping on the first page would put
  // the reader at the bottom of it and leave them there. A layout effect, so the column is
  // scrolled before the page holding the turn is ever painted at its top.
  //
  // Where the recording can be played and the turn carries a time, the citation is a seek: the
  // clock is put at the turn's start so a press plays the words being cited, and the column lands
  // where following keeps the turn being spoken, so the press moves nothing on screen. The seek
  // itself is asked of the player from a passive effect (`pendingLandingSeek`): the player takes
  // up a recording in an effect of its own, and a seek asked before that effect has run is refused
  // — which is the case whenever the landing happens in the commit that enables the player, as it
  // does when the cited turn is on the first page. A turn with no time, or a recording the browser
  // cannot play, lands with the turn's header at the top and following suspended, as a reader's
  // own scroll leaves it: the next explicit seek re-engages it.
  const landedOpening = useRef<string | null>(null);
  const opening =
    open && artifactId !== null && citedBlockIndex !== null
      ? `${artifactId}|${citedBlockIndex}`
      : null;
  const [pendingLandingSeek, setPendingLandingSeek] = useState<number | null>(null);
  /** The landing already put the column where its seek's follow would, so that follow adopts it. */
  const landingPlacedColumn = useRef(false);

  useLayoutEffect(() => {
    if (opening === null || citedBlockIndex === null) {
      landedOpening.current = null;
      return;
    }
    if (landedOpening.current === opening) return;
    const located = locateFocusTurn(document.turns, citedBlockIndex);
    if (!located.reached && transcript.isLoading) return;
    landedOpening.current = opening;
    if (located.turnKey === null) {
      setFollowSuspended(true);
      return;
    }
    const citedTurn =
      hasPlayableMedia && !isPlaybackUnsupported
        ? (playableTurns.find((turn) => turn.key === located.turnKey) ?? null)
        : null;
    if (citedTurn === null) {
      setFollowSuspended(true);
      jumpToTurn(located.turnKey, { instant: true });
      return;
    }
    landingPlacedColumn.current = true;
    scrollToFollow(citedTurn.key, 0, true);
    setJumpedTurnKey(citedTurn.key);
    setPendingLandingSeek(citedTurn.startSeconds);
  }, [
    citedBlockIndex,
    document,
    hasPlayableMedia,
    isPlaybackUnsupported,
    jumpToTurn,
    opening,
    playableTurns,
    scrollToFollow,
    transcript.isLoading,
  ]);

  useEffect(() => {
    if (pendingLandingSeek === null) return;
    player.seek(pendingLandingSeek);
    setPendingLandingSeek(null);
  }, [pendingLandingSeek, player.seek]);

  // The boundary: a new turn is being spoken, or somebody asked to be somewhere. A drag moves the
  // highlight and the rail and nothing else — the reader is watching the bar, and a column moving
  // under a dragging pointer is unreadable.
  useEffect(() => {
    if (!hasPlayableMedia) return;
    const turnChanged = followed.current.turnKey !== activeTurnKey;
    const seeked = followed.current.seekNonce !== player.seekNonce;
    if (!turnChanged && !seeked) return;
    followed.current = { turnKey: activeTurnKey, seekNonce: player.seekNonce };
    if (player.isScrubbing) return;
    if (seeked) setFollowSuspended(false);
    else if (followSuspended) return;
    if (activeTurnKey === null) return;
    if (seeked && landingPlacedColumn.current) {
      landingPlacedColumn.current = false;
      return;
    }
    scrollToFollow(activeTurnKey, 0, reducedMotion);
  }, [
    activeTurnKey,
    followSuspended,
    hasPlayableMedia,
    player.isScrubbing,
    player.seekNonce,
    reducedMotion,
    scrollToFollow,
  ]);

  // A turn taller than the column cannot sit still and be read, so it is scrolled through at the
  // rate it is spoken. Only for those turns: one that fits was placed at its anchor and stays.
  // These are per-frame writes of a few pixels each, and they stand aside while the boundary's
  // travel is still in flight — two things writing `scrollTop` in the same frame is the jump this
  // whole arrangement exists to avoid. Once the travel settles, the walk carries on from where it
  // landed.
  useEffect(() => {
    if (!hasPlayableMedia || reducedMotion || followSuspended || player.isScrubbing) return;
    if (activeTurnKey === null || player.status !== "playing" || scroll.isAnimating()) return;
    const scroller = scrollRef.current;
    const target = turnRefs.current.get(activeTurnKey);
    if (!scroller || !target || target.offsetHeight <= scroller.clientHeight) return;
    scrollToFollow(activeTurnKey, activeProgress, true);
  }, [
    activeProgress,
    activeTurnKey,
    followSuspended,
    hasPlayableMedia,
    player.isScrubbing,
    player.status,
    reducedMotion,
    scroll,
    scrollToFollow,
  ]);

  const resumeFollowing = useCallback(() => {
    setFollowSuspended(false);
    if (activeTurnKey === null) return;
    followed.current = { turnKey: activeTurnKey, seekNonce: player.seekNonce };
    scrollToFollow(activeTurnKey, activeProgress, reducedMotion);
  }, [activeProgress, activeTurnKey, player.seekNonce, reducedMotion, scrollToFollow]);

  const showJumpToPlaying =
    hasPlayableMedia && followSuspended && isSounding && activeTurn !== null;

  /**
   * The dialog's own keys, and only where there is something to play.
   *
   * Space is the one that has to be careful: inside the speaker rename box it is a space in a
   * name, so a press that starts in a text field is left entirely alone. The scrubber answers the
   * arrow keys itself when it holds the focus and stops them here.
   */
  const handleDialogKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!hasPlayableMedia || event.defaultPrevented || isTextEntry(event.target)) return;
      const step = event.shiftKey ? SEEK_STEP_LARGE_SECONDS : SEEK_STEP_SECONDS;
      if (event.key === " " || event.key === "Spacebar") player.toggle();
      else if (event.key === "ArrowLeft") player.seek(player.currentTime - step);
      else if (event.key === "ArrowRight") player.seek(player.currentTime + step);
      else if (event.key === "ArrowUp") player.jumpToTurnBoundary("back");
      else if (event.key === "ArrowDown") player.jumpToTurnBoundary("forward");
      else return;
      event.preventDefault();
    },
    [hasPlayableMedia, player]
  );

  // The clock moves every animation frame, so the two callbacks every turn block holds have to be
  // stable or `memo` would repaint the whole column sixty times a second. They read what they need
  // through refs kept current on each render.
  const playerRef = useRef(player);
  playerRef.current = player;
  const activeTurnKeyRef = useRef(activeTurnKey);
  activeTurnKeyRef.current = activeTurnKey;
  const isSoundingRef = useRef(isSounding);
  isSoundingRef.current = isSounding;

  const seekToTurn = useCallback((turnKey: number, startSeconds: number) => {
    if (activeTurnKeyRef.current === turnKey && isSoundingRef.current) {
      playerRef.current.pause();
      return;
    }
    playerRef.current.seek(startSeconds, { play: true });
  }, []);

  const registerTurn = useCallback((turnKey: number, element: HTMLElement | null) => {
    if (element === null) turnRefs.current.delete(turnKey);
    else turnRefs.current.set(turnKey, element);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="transcript-dialog"
        showCloseButton={false}
        onKeyDown={handleDialogKeyDown}
        size="reading"
      >
        <DialogHeader className="flex shrink-0 flex-row items-start justify-between gap-3 border-border border-b px-5 pt-4 pb-3 min-[840px]:px-6 min-[840px]:pt-[18px] min-[840px]:pb-4">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <DialogTitle className="truncate font-semibold text-base">
              {transcript.name ?? name}
            </DialogTitle>
            {/* Nothing is known yet — the word keeps the header at the height it will settle at
                once the first page lands. */}
            {meta.length === 0 ? (
              <DialogDescription data-testid="transcript-meta">Transcript</DialogDescription>
            ) : (
              <DialogDescription
                data-testid="transcript-meta"
                render={<dl />}
                className="flex flex-wrap items-center gap-2.5"
              >
                {meta.map((pair, index) => (
                  <Fragment key={pair.key}>
                    {index === 0 ? null : (
                      <span aria-hidden className="h-2.5 w-px shrink-0 bg-border" />
                    )}
                    <div data-meta={pair.key} className="flex items-center gap-[5px]">
                      <dt className="font-normal text-[0.71875rem]/[1.5] text-neutral-dim">
                        {pair.label}
                      </dt>
                      <dd className="font-medium text-[0.71875rem]/[1.5] text-neutral-muted-fg">
                        {pair.value}
                      </dd>
                    </div>
                  </Fragment>
                ))}
              </DialogDescription>
            )}
          </div>
          <DialogClose
            data-testid="transcript-dialog-close"
            render={<Button variant="ghost" size="icon-sm" className="-mt-1 shrink-0" />}
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        {/* The legend is about speakers, so a transcript nobody was told apart in has none —
            even when it has a timeline. */}
        {speakers.length === 0 ? null : (
          <TranscriptSpeakerLegend
            speakers={speakers}
            speakerNames={speakerNames}
            onRename={renameSpeaker}
            isRenaming={isRenaming}
            activity={activity}
            isTimelineExpanded={isExpanded}
            onToggleTimeline={toggleTimeline}
          />
        )}
        {isExpanded && activity !== null ? (
          <TranscriptActivityPanel
            activity={activity}
            speakerNames={speakerNames}
            onJumpToTurn={jumpToTurn}
            transport={
              hasPlayableMedia && !isPlaybackUnsupported
                ? {
                    positionSeconds: player.pendingSeek ?? player.currentTime,
                    // The playhead, and the seeking that goes with it, exist only while sound is
                    // coming out. Silent, the panel is what it is for: who spoke when.
                    isSounding,
                    // A press on a track moves the clock and nothing else: what is playing keeps
                    // playing, from there.
                    onSeek: (seconds) => player.seek(seconds),
                    onScrubStart: player.beginScrub,
                    onScrubEnd: player.endScrub,
                  }
                : null
            }
          />
        ) : null}

        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            ref={scrollRef}
            data-testid="transcript-scroll"
            className="min-h-0 flex-1 overflow-y-auto px-5 py-4 min-[840px]:px-6 min-[840px]:py-5"
          >
            {/* One reading column, centred, whatever the panel's width — a transcript read edge to
                edge on a 1100px dialog is a line nobody's eye can track back. */}
            <div className="mx-auto flex w-full max-w-[46rem] flex-col gap-4">
              {/* Above the words, because it is about the words: the transcript being read was
                  replaced and what is arriving is a fresh read of the one that replaced it. */}
              {transcript.reloadedNote === null ? null : (
                <p
                  data-testid="transcript-reloaded"
                  className="text-muted-foreground text-xs/relaxed"
                >
                  {transcript.reloadedNote}
                </p>
              )}
              {document.turns.map((turn) => (
                <TranscriptTurnBlock
                  key={turn.key}
                  turn={turn}
                  speakerNames={speakerNames}
                  isJumpTarget={jumpedTurnKey === turn.key}
                  hasPlayableMedia={hasPlayableMedia}
                  isActive={activeTurnKey === turn.key}
                  isSounding={activeTurnKey === turn.key && isSounding}
                  isPlaybackUnsupported={isPlaybackUnsupported}
                  progress={activeTurnKey === turn.key ? activeProgress : 0}
                  onSeekToTurn={seekToTurn}
                  registerTurn={registerTurn}
                />
              ))}
              {transcript.error !== null ? (
                <p data-testid="transcript-error" className="text-destructive text-xs/relaxed">
                  {transcript.error}
                </p>
              ) : null}
              {transcript.isLoading ? (
                <LoadingRow
                  data-testid="transcript-loading"
                  message={
                    hasTurns ? "Loading the rest of the transcript…" : "Loading the transcript…"
                  }
                />
              ) : null}
              {!transcript.isLoading && !hasTurns && transcript.error === null ? (
                <p data-testid="transcript-empty" className="text-muted-foreground text-xs/relaxed">
                  This transcript is empty.
                </p>
              ) : null}
            </div>
          </div>
          {/* Following never fights back: once the reader has scrolled, this is the only way the
              column moves itself again, and it is an offer rather than a correction. */}
          {showJumpToPlaying ? (
            <Button
              size="sm"
              data-testid="transcript-jump-to-playing"
              className="-translate-x-1/2 absolute bottom-3 left-1/2 h-7 px-3 text-xs shadow-md"
              onClick={resumeFollowing}
            >
              Jump to playing
            </Button>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-col gap-2.5 border-border border-t px-5 pt-3 pb-4 min-[840px]:flex-row min-[840px]:items-center min-[840px]:justify-end min-[840px]:gap-2.5 min-[840px]:px-6 min-[840px]:py-3">
          {hasPlayableMedia ? (
            <>
              <TranscriptPlayerBar player={player} totalSeconds={lengthSeconds} />
              <span aria-hidden className="hidden h-5 w-px shrink-0 bg-border min-[840px]:block" />
            </>
          ) : null}
          <Button
            variant="outline"
            data-testid="transcript-dialog-cancel"
            className="h-11 flex-1 min-[840px]:h-8 min-[840px]:flex-none"
            onClick={() => onOpenChange(false)}
          >
            Close
          </Button>
          <Button
            data-testid="transcript-dialog-download"
            className="h-11 flex-1 min-[840px]:h-8 min-[840px]:flex-none"
            // Nothing to write yet, or the last press is still paging.
            disabled={isDownloading || !hasTurns}
            onClick={onDownload}
          >
            <DownloadIcon className="size-3.5" aria-hidden />
            Download transcript
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One turn.
 *
 * The speaker is what decides the shape, and it decides it per turn rather than per transcript:
 * a labelled turn is a block behind a coloured rule — the chip carries the speaker and the rule
 * carries the same accent down the whole turn, so the eye follows one voice through a page
 * without reading a single label — and an unlabelled one is a paragraph with its timecode above
 * it, or a paragraph and nothing else where there is no time either. An undiarized transcript is
 * therefore a column of the second shape, with no speaker chrome anywhere, because none of its
 * turns names anybody.
 *
 * ── The turn being spoken ────────────────────────────────────────────────────
 * Where the recording can be played, the turn holding the playhead takes a wash of its own — not
 * a speaker colour, because "this is where you are" is true of whichever voice is talking — its
 * timecode goes blue, and its own accent rule fills top-down at the rate the turn is being spoken.
 * The rail is the progress bar: a per-turn bar somewhere else would be a second thing saying what
 * the words already show.
 *
 * `memo` is load-bearing rather than tidiness: the clock moves every animation frame, and a
 * five-hour meeting is thousands of these. Only the turn whose props actually changed repaints.
 */
const TranscriptTurnBlock = memo(function TranscriptTurnBlock({
  turn,
  speakerNames,
  isJumpTarget,
  hasPlayableMedia,
  isActive,
  isSounding,
  isPlaybackUnsupported,
  progress,
  onSeekToTurn,
  registerTurn,
}: {
  turn: TranscriptTurn;
  speakerNames: TranscriptSpeakerNames;
  isJumpTarget: boolean;
  hasPlayableMedia: boolean;
  isActive: boolean;
  isSounding: boolean;
  /** This browser cannot decode the recording's audio, so every glyph is muted and inert. */
  isPlaybackUnsupported: boolean;
  /** `0`–`1` through this turn, and `0` for every turn that is not the active one. */
  progress: number;
  onSeekToTurn: (turnKey: number, startSeconds: number) => void;
  registerTurn: (turnKey: number, element: HTMLElement | null) => void;
}) {
  const speaker = turn.header?.speaker;
  const timecode = turn.header === null ? null : formatTranscriptTurnTimecode(turn.header);
  const startSeconds = turn.header?.startSeconds;
  // A turn the vendor placed at no time cannot be played from: there is nowhere to start.
  const playFrom =
    hasPlayableMedia && typeof startSeconds === "number" && Number.isFinite(startSeconds)
      ? startSeconds
      : null;

  return (
    <div
      ref={(element) => registerTurn(turn.key, element)}
      data-testid="transcript-turn"
      data-speaker={speaker ?? undefined}
      data-jump-target={isJumpTarget || undefined}
      data-active={isActive || undefined}
      style={speaker === undefined ? undefined : speakerPaletteStyle(speaker)}
      className={cn(
        "relative flex flex-col gap-1",
        // The rule is the turn-taking made visible; without a speaker there is no turn-taking to
        // show and the column stays clean.
        speaker === undefined
          ? null
          : cn(
              "border-l-2 pl-3.5",
              isActive ? "border-(--speaker-tint)" : "border-(--speaker-border)"
            ),
        // A jump lands somewhere in the middle of a page of prose, so the turn says where.
        isJumpTarget && "bg-accent/60",
        isActive && "bg-transcript-active-turn"
      )}
    >
      {speaker === undefined || !isActive ? null : (
        <span
          data-testid="transcript-turn-rail"
          aria-hidden
          className="-left-0.5 absolute top-0 w-0.5 bg-(--speaker-bar)"
          style={{ height: `${Math.min(100, Math.max(0, progress * 100))}%` }}
        />
      )}
      {speaker === undefined && timecode === null ? null : (
        <div className="flex items-center gap-2">
          {speaker === undefined ? null : (
            <span
              data-testid="transcript-speaker"
              className="bg-(--speaker-tint) px-1.5 py-0.5 font-medium text-[11px] text-(--speaker-ink)"
            >
              {transcriptSpeakerDisplayLabel(speaker, speakerNames)}
            </span>
          )}
          {timecode === null ? null : playFrom !== null ? (
            <TranscriptTurnPlayButton
              timecode={timecode}
              isActive={isActive}
              isSounding={isSounding}
              isUnsupported={isPlaybackUnsupported}
              onPress={() => onSeekToTurn(turn.key, playFrom)}
            />
          ) : (
            <span
              data-testid="transcript-timecode"
              className={cn(
                "text-[11px]",
                isActive ? "font-medium text-primary" : "text-muted-foreground"
              )}
            >
              {timecode}
            </span>
          )}
        </div>
      )}
      <p className="whitespace-pre-wrap text-foreground text-sm/relaxed">{turn.text}</p>
    </div>
  );
});
